// ZITADEL token claim shapes + extraction helpers.
//
// ZITADEL emits roles under a namespaced claim
// `urn:zitadel:iam:org:project:roles` (a map of roleKey -> { orgId: orgDomain }),
// and the user's resource owner (home org) under
// `urn:zitadel:iam:user:resourceowner:id`. We normalize both into a flat shape so
// the rest of the codebase never deals with ZITADEL's URN claim names directly.

import type { JWTPayload } from 'jose';

export const ZITADEL_ROLES_CLAIM = 'urn:zitadel:iam:org:project:roles';
export const ZITADEL_RESOURCE_OWNER_ID_CLAIM =
  'urn:zitadel:iam:user:resourceowner:id';
export const ZITADEL_RESOURCE_OWNER_DOMAIN_CLAIM =
  'urn:zitadel:iam:user:resourceowner:primary_domain';

export type ZitadelClaims = JWTPayload & {
  sub: string;
  email?: string;
  email_verified?: boolean;
  [ZITADEL_ROLES_CLAIM]?: Record<string, Record<string, string>>;
  [ZITADEL_RESOURCE_OWNER_ID_CLAIM]?: string;
  [ZITADEL_RESOURCE_OWNER_DOMAIN_CLAIM]?: string;
};

export type NormalizedClaims = {
  subject: string;
  email: string;
  emailVerified: boolean;
  /** ZITADEL home org id (resource owner). */
  zitadelOrgId: string;
  primaryDomain?: string;
  /** Flattened list of granted role keys. */
  roles: string[];
};

export function normalizeClaims(claims: ZitadelClaims): NormalizedClaims {
  const rolesClaim = claims[ZITADEL_ROLES_CLAIM] ?? {};
  const roles = Object.keys(rolesClaim);

  const zitadelOrgId =
    claims[ZITADEL_RESOURCE_OWNER_ID_CLAIM] ??
    // Fallback: some flows put the org id on the first role's grant map.
    Object.values(rolesClaim)[0] != null
      ? Object.keys(Object.values(rolesClaim)[0] ?? {})[0]
      : '';

  return {
    subject: claims.sub,
    email: claims.email ?? '',
    emailVerified: Boolean(claims.email_verified),
    zitadelOrgId:
      claims[ZITADEL_RESOURCE_OWNER_ID_CLAIM] ?? zitadelOrgId ?? '',
    primaryDomain: claims[ZITADEL_RESOURCE_OWNER_DOMAIN_CLAIM],
    roles,
  };
}
